/**
 * WHERE A SIGN-IN SHOULD LAND — the browser's copy of the filter, and the authoritative one (#968).
 *
 * A shared Hub link (`/projects/2025-014?doc=d-om#documents`) opened on a lapsed session was lost:
 * `useSession` bounced to `/sign-in` capturing nothing, and `ConfirmSignIn` hard-coded
 * `window.location.assign('/')`. The person landed on the project list with nothing telling them a
 * link had been dropped — canon R9's share verb losing to the drive it replaces.
 *
 * The destination is now captured here, carried through the magic-link round trip inside the signed
 * token (so it survives finishing in a different tab or browser), and handed back to this module to be
 * checked once more before anything navigates.
 *
 * 🔴 THE CANDIDATE IS ATTACKER-SUPPLIED INPUT ON AN AUTHENTICATION FLOW, on the way in (a `?next=`
 * anyone can put in a URL) and on the way back (a claim in a token). An unvalidated one is an open
 * redirect on the sign-in route — a worse defect than the lost link. That the api signed it proves we
 * minted it; it does not prove it is safe to follow.
 *
 * 🔴 VALIDATE THE STRING YOU RETURN, NOT THE STRING YOU WERE GIVEN. The first version of this file
 * checked the raw candidate and then returned `url.pathname + search + hash` — and those are DIFFERENT
 * STRINGS, because the URL parser normalises. `/.//evil.example/x` passed every rule, resolved to an
 * origin that was ours, and came back out as `//evil.example/x`, which `window.location.assign` reads
 * as protocol-relative and follows to `https://evil.example`. `/x/%2e%2e//evil.example` did the same
 * through percent-encoded traversal, and `/./auth/confirm` walked straight past the refused-prefix
 * list. All three passed the api's copy as well, so the "three checks" were three checks of a string
 * nothing ever used. Every rule now runs on the normalised path, which is the one that navigates.
 *
 * ⚠️ The api holds the same filter (`packages/api/src/return-path.ts`) — the web package takes no
 * `@ggpathos/*` imports by design. This copy is authoritative because this is the only place a
 * navigation happens, and only this one can resolve against the live origin; the api's normalises
 * against a fixed synthetic base instead, so it enforces the same shape without needing to know which
 * front end asked.
 */

/** The longest path we carry — long enough for a deep link with a query, short enough to bound the token. */
const MAX_RETURN_PATH_LENGTH = 512;

/**
 * Control characters, including the ones a URL parser **strips** rather than rejects: a raw tab inside
 * a path is silently removed during parsing, so the string that is used is not the string that was
 * checked. Refusing the character is more predictable than reasoning about which ones survive.
 */
// eslint-disable-next-line no-control-regex -- matching control characters IS the rule here.
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;

/** Landing back on the auth flow after completing it is a loop at best. */
const REFUSED_PREFIXES = ['/auth/', '/sign-in'] as const;

/** The location fields we need. Structural, so tests need no `window`. */
export interface CapturedLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

/**
 * Whether a root-relative path is a safe in-app destination.
 *
 * Applied TWICE — to the raw candidate and to the parser's output — because the two catch different
 * things and neither is sufficient alone:
 *
 * - the **raw** pass catches what the parser silently REWRITES AWAY: a backslash it turns into a
 *   slash, a control character it strips, a `..` segment it resolves;
 * - the **output** pass catches what the parser CREATES: a leading `//` produced by normalising
 *   `/.//evil.example`, an auth route reached behind a `/./`, a length inflated by percent-encoding.
 *
 * The first version ran only the raw pass and returned the output. Each rule refuses a way out of the
 * origin:
 *
 * | Refused              | Why                                                                        |
 * | -------------------- | -------------------------------------------------------------------------- |
 * | `//evil.example`     | Protocol-relative — a URL wearing a path's clothes, and it starts with `/` |
 * | a backslash          | A browser normalises it to `/`, so `/\evil.example` IS `//evil.example`    |
 * | a control character  | Some are stripped while parsing, changing the string after it was checked  |
 * | a `..` segment       | Traversal. Harmless in a SPA route; refused so the rule needs no caveat    |
 * | `/auth/`, `/sign-in` | Back into the auth flow, or a sign-in loop                                 |
 */
function isSafePathShape(path: string): boolean {
  if (path.length === 0 || path.length > MAX_RETURN_PATH_LENGTH) return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.includes('\\')) return false;
  if (CONTROL_CHARS.test(path)) return false;
  if (path.split(/[/?#]/).includes('..')) return false;
  if (REFUSED_PREFIXES.some((prefix) => path.startsWith(prefix))) return false;
  return true;
}

/**
 * The candidate as a safe in-app destination for `origin`, otherwise `undefined`.
 *
 * Three steps, in this order and for this reason:
 *
 * 1. **The shape rules on the raw string** — refusing what the parser would quietly rewrite away, and
 *    everything that is not root-relative, so `javascript:` and `https://evil.example` never reach the
 *    parser at all.
 * 2. **Resolve against the live origin.** Anything that lands elsewhere is not ours, whatever it
 *    looked like — this is what catches `/\evil.example`, which resolves to `https://evil.example`.
 * 3. **The same shape rules on the NORMALISED path**, which is the string that will actually navigate.
 *    Step 1 is not a substitute for step 3, nor step 3 for step 1: the parser both removes things
 *    (a backslash, a control character, a `..`) and creates them (a leading `//` out of `/.//`).
 */
export function safeReturnPath(candidate: unknown, origin: string): string | undefined {
  if (typeof candidate !== 'string') return undefined;
  if (!isSafePathShape(candidate)) return undefined;

  let resolved: URL;
  try {
    resolved = new URL(candidate, origin);
  } catch {
    return undefined;
  }
  if (resolved.origin !== origin) return undefined;

  const path = `${resolved.pathname}${resolved.search}${resolved.hash}`;
  return isSafePathShape(path) ? path : undefined;
}

/**
 * The sign-in URL to send someone to, carrying where they were trying to go.
 *
 * ⚠️ For a session that LAPSED — not for signing out. A sign-out that captured the current page would
 * hand the next person at that browser a link straight back into it, and would make "sign out" mean
 * "sign out and come back here", which is not what the button says. The two sign-out call sites pass
 * through `/sign-in` bare, deliberately.
 */
export function signInPathFor(location: CapturedLocation, origin: string): string {
  const attempted = safeReturnPath(
    `${location.pathname}${location.search}${location.hash}`,
    origin,
  );
  // `/` carries no information — the sign-in page's own default already lands there.
  if (attempted === undefined || attempted === '/') return '/sign-in';
  return `/sign-in?next=${encodeURIComponent(attempted)}`;
}
